import React from 'react';
import useLanguage from '../../hooks/useLanguage';
import contactData from '../../data/contact.json';
import { FiMapPin, FiPhone, FiMail, FiNavigation } from 'react-icons/fi';
import Card from '../common/Card';

/**
 * LocationInfo Component
 * Displays headquarters and R&D center location information
 */
const LocationInfo = () => {
  const { language } = useLanguage();
  const { contactInfo } = contactData;

  // Location data (본사 및 연구소)
  const locations = [
    {
      id: 'headquarters',
      name: language === 'ko' ? '본사' : 'Headquarters',
      address: language === 'ko' ? contactInfo.address : contactInfo.addressEn,
      postalCode: '06234',
      phone: contactInfo.phone,
      email: contactInfo.email,
      directions: language === 'ko'
        ? '지하철 2호선 역삼역 3번 출구에서 도보 10분\n또는 지하철 9호선 봉은사역 1번 출구에서 도보 8분'
        : 'Subway Line 2, Yeoksam Station Exit 3, 10 min walk\nor Subway Line 9, Bongeunsa Station Exit 1, 8 min walk',
      icon: FiMapPin,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'rnd-center',
      name: language === 'ko' ? '연구개발센터' : 'R&D Center',
      address: language === 'ko'
        ? '경기도 성남시 분당구 판교역로 235, 테크타워 12층'
        : '12F Tech Tower, 235 Pangyoyeok-ro, Bundang-gu, Seongnam-si, Gyeonggi-do, Korea',
      postalCode: '13494',
      phone: '+82-31-5678-1234',
      email: 'rnd@radartech.com',
      directions: language === 'ko'
        ? '신분당선 판교역 2번 출구 직결\n또는 경부고속도로 → 판교IC에서 5분'
        : 'Sinbundang Line, Pangyo Station Exit 2, Direct connection\nor Gyeongbu Expressway → 5 min from Pangyo IC',
      icon: FiMapPin,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Section Title */}
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          {language === 'ko' ? '오시는 길' : 'Directions'}
        </h2>
        <p className="text-gray-600">
          {language === 'ko'
            ? '본사와 연구개발센터의 위치 및 찾아오시는 방법입니다.'
            : 'Locations and directions to our headquarters and R&D center.'}
        </p>
      </div>

      {/* Location Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {locations.map((location) => (
          <Card key={location.id} className="p-6 hover:shadow-xl transition-shadow duration-300">
            {/* Location Name */}
            <div className={`flex items-center space-x-3 mb-4 pb-4 border-b-2 ${location.color.replace('text', 'border')}`}>
              <div className={`${location.bgColor} p-3 rounded-full`}>
                <location.icon className={`w-6 h-6 ${location.color}`} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                {location.name}
              </h3>
            </div>

            {/* Address */}
            <div className="space-y-4">
              {/* Full Address */}
              <div className="flex items-start space-x-3">
                <FiMapPin className="w-5 h-5 text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    {language === 'ko' ? '주소' : 'Address'}
                  </p>
                  <p className="text-gray-900 leading-relaxed">
                    {location.address}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {language === 'ko' ? '우편번호' : 'Postal Code'}: {location.postalCode}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-3">
                <FiPhone className="w-5 h-5 text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    {language === 'ko' ? '전화' : 'Phone'}
                  </p>
                  <p className="text-gray-900">
                    {location.phone}
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-3">
                <FiMail className="w-5 h-5 text-gray-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    {language === 'ko' ? '이메일' : 'Email'}
                  </p>
                  <p className="text-gray-900">
                    {location.email}
                  </p>
                </div>
              </div>

              {/* Directions */}
              <div className={`${location.bgColor} p-4 rounded-md mt-4`}>
                <div className="flex items-start space-x-3">
                  <FiNavigation className={`w-5 h-5 ${location.color} flex-shrink-0 mt-1`} />
                  <div>
                    <p className={`text-sm font-semibold ${location.color} mb-2`}>
                      {language === 'ko' ? '대중교통 이용' : 'Public Transportation'}
                    </p>
                    <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {location.directions}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Additional Info */}
      <Card className="p-6 bg-blue-50 border-l-4 border-blue-600 mt-8">
        <div className="flex items-start space-x-3">
          <FiMapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <p className="font-semibold text-blue-900 mb-1">
              {language === 'ko' ? '방문 예약' : 'Visit Reservation'}
            </p>
            <p className="text-sm text-blue-800">
              {language === 'ko'
                ? '방문 전 미리 연락주시면 주차 및 안내를 도와드리겠습니다. 전화 또는 이메일로 방문 일정을 예약해주세요.'
                : 'Please contact us in advance for parking assistance and directions. Reserve your visit by phone or email.'}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LocationInfo;
